import { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../../../interceptors";
import { getInterview } from "../../../api/interview";

// Inline interview picker for /admin/compare.
//
// Backed by GET /job-posting/job-details/jobs/interviews — same source the
// /admin/interviews screen uses. We flatten the jobs→interviews tree, keep
// only COMPLETED sessions (those are the only ones with a comparable score),
// and enrich each row with `overall_score` from the interview service's
// report-json endpoint so the picker shows the same score chips users see
// in the interviews table.
//
// Selection key is the interview `id` — the new compare endpoint consumes
// interview_ids, not candidate userIds.
const MAX_SELECT = 5;
const MIN_SELECT = 2;

const isCompleted = (s) => String(s ?? "").toUpperCase() === "COMPLETED";

export default function CandidatePicker({
  initialIds = [],
  onConfirm,
  onCancel,
  asModal = false,
}) {
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() => new Set(initialIds));
  // Score cache mirrors the pattern used in interviews-redesign — keyed by
  // interview id so per-row enrichment survives re-renders.
  const scoreCacheRef = useRef(new Map());
  const scoreInFlightRef = useRef(new Set());
  const [, setScoreVersion] = useState(0);

  // Load the interview list (same endpoint as /admin/interviews).
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await axiosInstance.get(
          "/job-posting/job-details/jobs/interviews",
        );
        const data = r.data?.data ?? r.data;
        let flat = [];
        if (
          Array.isArray(data) &&
          data.length &&
          Array.isArray(data[0]?.interviews)
        ) {
          flat = data.flatMap((j) =>
            (j.interviews || []).map((iv) => ({
              ...iv,
              jobId: iv.jobId ?? j.jobId,
              position: iv.position || j.position,
            })),
          );
        } else if (Array.isArray(data?.interviews)) {
          flat = data.interviews;
        } else if (Array.isArray(data?.list)) {
          flat = data.list;
        } else if (Array.isArray(data)) {
          flat = data;
        }
        if (!cancelled) setRows(flat.filter((iv) => isCompleted(iv.status)));
      } catch (e) {
        if (!cancelled)
          setError(e?.message || e?.data?.error || "Failed to load interviews");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Enrich each COMPLETED row with overall_score. Same dedupe/cache pattern
  // as the interviews-redesign table.
  useEffect(() => {
    const missing = rows.filter(
      (r) => r?.id != null && !scoreInFlightRef.current.has(r.id),
    );
    if (missing.length === 0) return;

    missing.forEach((r) => {
      scoreInFlightRef.current.add(r.id);
      scoreCacheRef.current.set(r.id, { status: "loading" });
    });
    setScoreVersion((v) => v + 1);

    const controller = new AbortController();
    let cancelled = false;

    Promise.all(
      missing.map(async (r) => {
        try {
          const res = await getInterview(r.id, "report-json", {
            signal: controller.signal,
          });
          const payload = res.data?.data ?? res.data ?? {};
          const report = payload.report ?? payload;
          scoreCacheRef.current.set(r.id, {
            status: "ready",
            score: report.overall_score ?? null,
          });
        } catch (err) {
          if (err?.name === "CanceledError" || err?.name === "AbortError") {
            scoreInFlightRef.current.delete(r.id);
            scoreCacheRef.current.delete(r.id);
            return;
          }
          scoreCacheRef.current.set(r.id, { status: "failed" });
        }
      }),
    ).then(() => {
      if (!cancelled) setScoreVersion((v) => v + 1);
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = `${r.name || ""} ${r.email || ""} ${r.position || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < MAX_SELECT) {
        next.add(id);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    if (selected.size < MIN_SELECT) return;
    onConfirm?.({ ids: Array.from(selected) });
  };

  const body = (
    <div className={asModal ? "w-[640px] max-w-[92vw] bg-white rounded-xl border border-slate-200 shadow-2xl overflow-hidden" : "bg-white border border-slate-200 rounded-xl overflow-hidden"}>
      <div className="px-4 py-3 border-b border-slate-200">
        <div className="text-sm font-semibold text-vf-base">Pick candidates to compare</div>
        <div className="text-[11px] text-vf-muted mt-0.5">
          Select {MIN_SELECT}–{MAX_SELECT} completed interviews. {selected.size} selected.
        </div>
      </div>

      <div className="p-4 border-b border-slate-100 flex flex-wrap gap-2 items-center">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name, email, or position"
          className="flex-1 min-w-[180px] px-2 py-1 border border-slate-300 rounded text-xs"
        />
      </div>

      <div className="max-h-[420px] overflow-y-auto">
        {loading && <div className="p-6 text-center text-sm text-vf-muted">Loading interviews…</div>}
        {error && (
          <div className="m-4 border border-red-200 bg-red-50 rounded p-3 text-xs text-red-700">{error}</div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div className="p-6 text-center text-sm text-vf-muted">No completed interviews to compare.</div>
        )}
        {!loading && !error && filtered.map((r) => {
          const checked = selected.has(r.id);
          const disabled = !checked && selected.size >= MAX_SELECT;
          const cached = scoreCacheRef.current.get(r.id);
          const score = cached?.score;
          return (
            <label
              key={r.id}
              className={`flex items-center gap-3 px-4 py-2 border-t border-slate-100 ${
                disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:bg-slate-50"
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={() => toggle(r.id)}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-vf-base truncate">{r.name || `Interview #${r.id}`}</div>
                <div className="text-[11px] text-vf-muted truncate">{r.email || "—"}</div>
              </div>
              <ScoreChip status={cached?.status} score={score} />
              {r.position && (
                <span className="text-[11px] text-vf-muted truncate max-w-[180px]" title={r.position}>
                  {r.position}
                </span>
              )}
            </label>
          );
        })}
      </div>

      <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between bg-slate-50">
        <div className="text-[11px] text-vf-muted">
          {filtered.length} of {rows.length} shown
        </div>
        <div className="flex gap-2">
          {onCancel && (
            <button onClick={onCancel} className="px-3 py-1.5 rounded border border-slate-300 text-xs">
              Cancel
            </button>
          )}
          <button
            onClick={handleConfirm}
            disabled={selected.size < MIN_SELECT}
            className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Compare {selected.size > 0 ? `(${selected.size})` : ""}
          </button>
        </div>
      </div>
    </div>
  );

  if (!asModal) return body;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()}>{body}</div>
    </div>
  );
}

function ScoreChip({ status, score }) {
  if (status === "loading") return <span className="text-[11px] text-slate-400">…</span>;
  if (score == null) return <span className="text-[11px] text-slate-400">—</span>;
  const s = Number(score);
  const cls =
    s >= 8 ? "bg-emerald-100 text-emerald-700"
    : s >= 6 ? "bg-amber-100 text-amber-700"
    : "bg-red-100 text-red-700";
  return (
    <span className={`px-1.5 py-0.5 rounded text-[11px] font-semibold tabular-nums ${cls}`}>
      {s.toFixed(1)}
    </span>
  );
}
